import React from 'react';
import { File as FileIcon, ExternalLink, X, FileVideo, Image as ImageIcon } from 'lucide-react';
import { youtubeIdFromUrl, vimeoIdFromUrl } from '../lib/mediaCapture';

/**
 * AttachmentTile — single attachment display tile.
 *
 * Renders based on attachment shape:
 *   - kind: 'media' with localUri → inline <video>/<img>
 *   - kind: 'media' without localUri → filename-only metadata line
 *   - kind: 'url' → YouTube/Vimeo iframe embed or external-link card
 *
 * No dataUrl, no cloud-hosted binary. localUri is device-local and only
 * present when the media is playable on this device (i.e., the user
 * captured it from this device and hasn't reloaded since, or on native
 * where Capacitor Filesystem persists it across sessions).
 *
 * Props:
 *   att     — attachment record
 *   onRemove — optional () => void
 *   compact — bool; smaller render for grid views
 */
const AttachmentTile = ({ att, onRemove, compact = false }) => {
  if (!att) return null;

  // URL-only attachment (pasted YouTube/Vimeo/etc.)
  if (att.kind === 'url') {
    return <UrlTile att={att} onRemove={onRemove} compact={compact} />;
  }

  // Media attachment with playable local source
  if (att.localUri && att.type?.startsWith('video/')) {
    return <MediaWrapper att={att} onRemove={onRemove} compact={compact}>
      <video controls preload="metadata" src={att.localUri} className="w-full h-full object-cover rounded" />
    </MediaWrapper>;
  }
  if (att.localUri && att.type?.startsWith('image/')) {
    return <MediaWrapper att={att} onRemove={onRemove} compact={compact}>
      <img alt={att.name} src={att.localUri} className="w-full h-full object-cover rounded" />
    </MediaWrapper>;
  }

  // Media metadata only — file lives on user's device but not playable here.
  // This is the steady state for web after reload, OR when the user is
  // viewing a session synced from a different device.
  return <FilenameTile att={att} onRemove={onRemove} compact={compact} />;
};

// ----- Subcomponents -----

const formatBytes = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
};

const RemoveButton = ({ onRemove }) => (
  onRemove ? (
    <button
      type="button"
      onClick={onRemove}
      aria-label="Remove attachment"
      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/75 hover:bg-enf-role-warning/80 text-enf-text-primary flex items-center justify-center transition-colors"
    >
      <X className="w-3.5 h-3.5" />
    </button>
  ) : null
);

const MediaWrapper = ({ att, onRemove, compact, children }) => (
  <div className={`relative overflow-hidden bg-enf-surface-mid rounded-md ${compact ? 'aspect-square' : 'aspect-video'}`}>
    {children}
    <RemoveButton onRemove={onRemove} />
    <div className="absolute bottom-1 left-1 right-1 text-[10px] font-mono text-enf-text-primary bg-black/60 px-1.5 py-0.5 rounded truncate">
      {att.name || 'capture'}
    </div>
  </div>
);

const FilenameTile = ({ att, onRemove, compact }) => {
  const isVideo = att.type?.startsWith('video/');
  const Icon = isVideo ? FileVideo : (att.type?.startsWith('image/') ? ImageIcon : FileIcon);
  return (
    <div className={`relative ${compact ? 'p-2.5' : 'p-3'} bg-enf-surface-mid border border-enf-surface-border rounded-md`}>
      <div className="flex items-start gap-2.5 min-w-0">
        <Icon className="w-5 h-5 text-enf-text-muted flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-mono text-enf-text-primary truncate" title={att.name}>{att.name || 'capture'}</div>
          <div className="text-[10px] text-enf-text-muted mt-0.5 flex flex-wrap gap-x-2">
            {att.size != null && <span>{formatBytes(att.size)}</span>}
            {att.capturedAt && <span>{formatDate(att.capturedAt)}</span>}
          </div>
          <div className="text-[10px] text-enf-text-muted mt-1 italic">On your device · open Photos to view</div>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove attachment"
            className="text-enf-text-muted hover:text-enf-role-warning p-1 -m-1 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

const UrlTile = ({ att, onRemove, compact }) => {
  const ytId = youtubeIdFromUrl(att.url);
  const vimeoId = vimeoIdFromUrl(att.url);

  if (ytId) {
    return (
      <div className={`relative overflow-hidden bg-enf-surface-mid rounded-md ${compact ? 'aspect-square' : 'aspect-video'}`}>
        <iframe
          title="YouTube video"
          src={`https://www.youtube.com/embed/${ytId}`}
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
        <RemoveButton onRemove={onRemove} />
      </div>
    );
  }
  if (vimeoId) {
    return (
      <div className={`relative overflow-hidden bg-enf-surface-mid rounded-md ${compact ? 'aspect-square' : 'aspect-video'}`}>
        <iframe
          title="Vimeo video"
          src={`https://player.vimeo.com/video/${vimeoId}`}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
        <RemoveButton onRemove={onRemove} />
      </div>
    );
  }
  // Generic external URL — no embed, just a clickable card.
  return (
    <div className={`relative ${compact ? 'p-2.5' : 'p-3'} bg-enf-surface-mid border border-enf-surface-border rounded-md`}>
      <a href={att.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2.5 min-w-0 group">
        <ExternalLink className="w-5 h-5 text-enf-role-info flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-enf-role-info group-hover:underline truncate" title={att.url}>{att.url}</div>
          <div className="text-[10px] text-enf-text-muted mt-0.5">External link · {formatDate(att.capturedAt)}</div>
        </div>
      </a>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove attachment"
          className="absolute top-2 right-2 text-enf-text-muted hover:text-enf-role-warning p-1"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default AttachmentTile;
